import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api';
import { toast } from 'react-toastify';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [authError, setAuthError] = useState(null);

  const isAuthenticated = !!user;

  const loadMe = useCallback(async () => {
    try {
      setAuthError(null);
      // Check if token exists in localStorage
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUser(null);
        return;
      }
      
      const me = await authAPI.me();
      // Expect minimal profile e.g. { id, name }
      setUser(me || null);
    } catch (err) {
      // Not authenticated or server error; keep user null
      setUser(null);
      // Clear invalid token
      localStorage.removeItem('authToken');
    } finally {
      setInitializing(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(async (username, password, rememberMe) => {
    try {
      setAuthError(null);
      const result = await authAPI.login(username, password, rememberMe);
      
      console.log('AuthContext: Login API response:', result);
      
      if (result?.accessToken) {
        console.log('AuthContext: Storing accessToken in localStorage');
        localStorage.setItem('authToken', result.accessToken);
        
        if (result?.expiresIn) {
          console.log('AuthContext: Storing token expiry');
          const expiryTime = Date.now() + (result.expiresIn * 1000);
          localStorage.setItem('tokenExpiry', expiryTime.toString());
        }
        
        // Set user as authenticated (we'll get user details from /me endpoint)
        console.log('AuthContext: Token stored, calling loadMe to get user details');
        toast.success('Login successful');
        
        await loadMe();
        console.log('AuthContext: loadMe completed');
        
        return { success: true };
      } else {
        console.log('AuthContext: No access token received in login response');
        throw new Error('Invalid login response - no access token received');
      }
    } catch (err) {
      console.error('Login failed:', err);
      const msg = err?.message || 'Login failed';
      setAuthError(msg);
      toast.error('Invalid credentials or unauthorized');
      return { success: false, error: msg };
    }
  }, [loadMe]);

  const logout = useCallback(async () => {
    try {
      // Get token for logout request
      const accessToken = localStorage.getItem('authToken');
      
      // Call logout API with token
      if (accessToken) {
        await authAPI.logout(accessToken);
      }
    } catch (_) {
      // ignore logout errors
    } finally {
      // Clear all tokens from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiry');
      setUser(null);
      toast.info('Logged out');
    }
  }, []);

  const value = {
    user,
    isAuthenticated,
    initializing,
    authError,
    login,
    logout,
    reloadUser: loadMe,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};